<template>
  <SubNavigation
    :title="department?.title"
    :children-links="department.parentNodePage?.children"
    :class="[
      {
        'mb-10 md:mb-15 xl:mb-17.5! 2xl:mb-20!':
          department?.featuredImage.length === 0 || !department?.perex,
      },
    ]"
  />
  <Container
    v-if="department?.featuredImage.length > 0 || department?.perex"
    id="department_hero"
    class="my-10 md:my-15 lg:px-10! xl:my-17.5 2xl:my-20 2xl:mb-20!"
  >
    <CascadingImages :images="department.featuredImage" />
    <p
      v-if="department?.perex"
      class="bp1680-text-47 border-b border-gray pb-3.75 font-header text-xl leading-[110%] text-pretty md:pb-6.25 md:text-2.5xl xl:pb-7 xl:text-4.5xl xl:leading-[105%]"
    >
      {{ department?.perex }}
    </p>
  </Container>

  <Container
    v-if="department?.announcements.length"
    class="bp1440-w-740 bp1680-w-863 mx-auto mb-11.25 flex w-full max-w-185 flex-col gap-3.75 p-0! px-5! md:mb-25 md:w-151.25 md:px-0! lg:w-153.75 xl:mb-17.5"
  >
    <NoticeList
      :title="t('common.announcements')"
      :notices="department.announcements"
    />
  </Container>

  <PersonsSection
    v-for="employeeSection in department?.employees"
    :title="employeeSection.title?.[locale]"
    :people="employeeSection.people"
  />

  <DynamicContentResolver
    v-if="department.content_blocks?.length > 0"
    :blocks="department.content_blocks"
  />

  <Container
    v-if="department.events?.length > 0"
    class="mb-20 md:mb-25 xl:mb-30"
  >
    <CardSlider :slides="department?.events" />
  </Container>

  <Archives
    v-if="department.archive_events?.length > 0"
    :years="[...resolveArchiveYears]"
    :archive-data="department.archive_events"
    class="bp1440-w-894 bp1680-w-1054 mb-20 px-5 md:mb-25 md:w-182 md:px-0 lg:w-194.5 xl:mb-30! 2xl:mb-35!"
  />

  <Container
    v-if="resolveRelatedPages?.length > 0"
    class="mb-21 lg:mb-37.5 xl:mb-42.5"
  >
    <RelatedPages
      :pages="resolveRelatedPages"
      :has-category-pages="department.category_departments.length > 0"
      :title="
        department.show_category_departments
          ? t('common.other_studios') +
            ' ' +
            department.departmentCategory?.name.toLowerCase()
          : ''
      "
      class="mb-0! px-0!"
    />
  </Container>
</template>

<script setup lang="ts">
import type { AppEvent, PageData } from '~/types'

const props = defineProps<{ pageData: PageData & AppEvent }>()

const { locale, t } = useI18n()
const setI18nParams = useSetI18nParams()
const breadcrumbsData = useBreadcrumbs()

const department = computed(() => props.pageData)

useDynamicSeo(department)

const resolveArchiveYears = computed(() => {
  return new Set(
    department.value.archive_events.map(
      (event: AppEvent & { year: number }) => event.year,
    ),
  )
})

const resolveRelatedPages = computed(() => {
  return [
    ...(department.value.show_category_departments
      ? department.value.category_departments
      : []),
    ...(department.value.category_links ? department.value.category_links : []),
  ]
})

watch(
  department,
  (val) => {
    if (val) {
      setI18nParams({
        cs: { slug: useGetSlugSegments(val, 'cs') },
        en: { slug: useGetSlugSegments(val, 'en') },
      })
    }
  },
  { immediate: true },
)

onMounted(() => {
  breadcrumbsData.value = props.pageData.breadcrumbs[locale.value]
})
</script>
